import { FieldPath, FieldValues, UseControllerProps, useController } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { cn } from "../../lib/utils";
import { FormFieldError } from "../FormFieldError";
import { CheckboxInput } from "../inputs/CheckboxInput";

type FormCheckboxOptionProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	label?: string;
	compact?: boolean;
	tooltip?: string;
	labelClassName?: string;
	errorMessage?: string;
	testId?: string;
};

export const FormCheckboxOption = <
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
>({
	control,
	defaultValue,
	disabled,
	name,
	compact,
	tooltip,
	label,
	labelClassName,
	errorMessage,
	testId,
}: FormCheckboxOptionProps<TFieldValues, TTransformedValues>) => {
	const {
		field,
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
		disabled,
	});

	return (
		<div className="relative">
			<div
				className={cn(
					"group flex items-center h-[29px] space-x-[14px] cursor-pointer",
					disabled ? "cursor-default" : "cursor-pointer",
					compact ? "space-x-[11px]" : undefined,
				)}
				onClick={(e) => {
					if (disabled) {
						e.preventDefault();
						e.stopPropagation();
						return;
					}
					field.onChange(!field.value);
				}}
			>
				<CheckboxInput
					className={cn(
						error
							? "outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2"
							: undefined,
					)}
					disabled={disabled}
					compact={compact}
					checked={field.value ?? false}
					testId={testId}
				/>
				{label !== undefined && (
					<span
						className={cn(
							"text-[15px] font-normal text-primary leading-[19px]",
							compact ? "text-[13px]" : undefined,
							labelClassName,
						)}
						data-tooltip-id={`tooltip-checkbox-${name}`}
						data-tooltip-content={tooltip}
					>
						{label}
						{tooltip && <Tooltip id={`tooltip-checkbox-${name}`} className="text-base" />}
					</span>
				)}
			</div>
			<FormFieldError
				error={error}
				errorMessage={errorMessage}
				className="top-[24px] left-[-8px]"
			/>
		</div>
	);
};
